/**
 * ArtifactCard — the inline stand-in for a document-shaped answer.
 *
 * A draft, timeline or memo is too long to read in a chat column, so the
 * transcript keeps a compact card and the pane holds the document. The card
 * is readable BEFORE the blob is hydrated: `intent` and `summary` come from
 * the SQL row, so a history turn labels itself without a fetch.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiArrowSquareOutLight,
  PiClockCounterClockwiseLight,
  PiScrollLight,
  PiNoteLight,
} from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import BrandLoader from '../../shared/brand/BrandLoader';
import { ResearchIntent } from '../types';
import { artifactTypeLabel } from '../artifacts';

const ICONS: Partial<Record<ResearchIntent, React.ReactNode>> = {
  draft: <PiScrollLight className="w-4 h-4" />,
  chronology: <PiClockCounterClockwiseLight className="w-4 h-4" />,
  memo: <PiNoteLight className="w-4 h-4" />,
  deep_research: <PiNoteLight className="w-4 h-4" />,
};

interface Props {
  intent: ResearchIntent;
  title: string;
  preview: string;
  /** Still being produced — the pane is showing its progress. */
  isStreaming?: boolean;
  /** This artifact is the one currently in the pane. */
  isOpen?: boolean;
  version?: number;
  versionCount?: number;
  onOpen: () => void;
}

const ArtifactCard: React.FC<Props> = ({
  intent,
  title,
  preview,
  isStreaming = false,
  isOpen = false,
  version,
  versionCount,
  onOpen,
}) => {
  const { t } = useTranslation();

  return (
    <GlassCard
      padding="sm"
      data-testid="artifact-card"
      data-intent={intent}
      data-open={isOpen}
      className={clsx(
        'reveal-item transition-all duration-200 ease-slide',
        isOpen ? 'border-champagne/50 shadow-md' : 'hover:shadow-md'
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid="artifact-open"
        className="flex items-start gap-3 w-full text-left"
      >
        <span className="mt-0.5 text-fg-muted flex-shrink-0" aria-hidden>
          {isStreaming ? <BrandLoader size="xs" /> : ICONS[intent] || <PiNoteLight className="w-4 h-4" />}
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-fg-strong truncate">{title}</span>
            <span className="px-1.5 py-0 rounded-full text-[10px] font-medium
                             bg-surface-sunken border border-hairline-soft text-fg-muted">
              {t(artifactTypeLabel(intent))}
            </span>
            {/* Re-asking produces a new turn, so versions are just turns. */}
            {versionCount && versionCount > 1 && version && (
              <span
                data-testid="artifact-version"
                className="px-1.5 py-0 rounded-full text-[10px] font-medium
                           bg-champagne/15 border border-champagne/40 text-fg-strong"
              >
                v{version}
              </span>
            )}
          </span>

          <span className="block mt-0.5 text-xs text-fg-muted line-clamp-2">
            {isStreaming && !preview ? t('Writing — open to follow along') : preview}
          </span>
        </span>

        <span
          className="flex items-center gap-1 flex-shrink-0 text-[11px] font-medium
                     text-fg-muted group-hover:text-champagne"
        >
          <PiArrowSquareOutLight className="w-3.5 h-3.5" aria-hidden />
          {isOpen ? t('Open') : t('Open')}
        </span>
      </button>
    </GlassCard>
  );
};

export default ArtifactCard;
